export interface ProgrammeBlock {
  title: string;
  duration: string;
  items: string[];
}

export interface FaqItem {
  question: string;
  answer: string;
}

export type CourseObjective =
  | "Découvrir l’IA"
  | "Utiliser l'IA"
  | "Gagner en productivité"
  | "Déployer l’IA"
  | "Automatiser"
  | "Innover";

export type CourseAudience =
  | "Tous publics"
  | "Managers & Direction"
  | "Équipes métiers"
  | "Équipes techniques";

export type CourseDuration =
  | "2h"
  | "1/2 journée"
  | "1 journée"
  | "2 journées"
  | "5 journées";

export type CourseFormat =
  | "Formation"
  | "Atelier"
  | "Conférence / Webinaire"
  | "Accompagnement";

export type CourseBadge =
  | "Best Seller"
  | "Nouveau"
  | "Très pratique"
  | "Managers"
  | "Sur mesure";

export interface Course {
  id: number;
  slug: string;
  title: string;
  description: string;
  image: string;
  level: string;
  duration: string;
  participants: string; // Note: Consider renaming to 'participants' (camelCase) for consistency
  category: string;
  benefits: string[]; // Utilisés comme objectifs pédagogiques
  price: string;
  publicCible?: string[];

  // Propriétés normalisées utilisées par les filtres du catalogue.
  objectives: CourseObjective[];
  audiences: CourseAudience[];
  durationOptions: CourseDuration[];
  format: CourseFormat;
  badges?: CourseBadge[];

  programme?: ProgrammeBlock[];
  faq?: FaqItem[];
}

// FAQ génériques, pertinentes pour la quasi-totalité des formations.
// À inclure dans le "faq" d'une formation via spread : "faq": [...faqStandard, { question, answer }, ...]
export const faqStandard: FaqItem[] = [
  {
    question: "Faut-il un niveau technique ou des prérequis pour suivre une formation IA ?",
    answer: "Non, aucun prérequis technique n'est nécessaire. Nos formations sont conçues pour être accessibles à tous, du débutant complet au manager déjà familiarisé avec les outils IA. Le niveau et le rythme sont adaptés au groupe grâce à un diagnostic de maturité réalisé en amont, ce qui évite le décrochage comme l'ennui."
  },
  {
    question: "Formation en intra dans mon entreprise ou à distance, est-ce possible ?",
    answer: "Oui. Upnov privilégie la formation intra-entreprise, dans vos locaux ou dans les nôtres, pour que toute l'équipe progresse ensemble sur des cas d'usage propres à ses métiers. Le distanciel et l'hybride sont également proposés, notamment pour les équipes réparties sur plusieurs sites."
  },
  {
    question: "Combien de temps dure une formation et quel est son tarif ?",
    answer: "La durée varie selon vos objectifs : de 2 heures pour un webinaire de sensibilisation à plus de 5 jours pour un parcours complet de transformation. Les tarifs débutent à 499 € par personne et par jour. Un devis personnalisé vous est proposé en fonction du nombre de participants, du format choisi et du niveau de personnalisation souhaité."
  }
];

export const coursesData: Course[] = [
    {
    "id": 2,
    "slug": "formation-ia-entreprise-pratique",
   "title": "L'IA en Entreprise : Formation Pratique et Accessible (1J)",
  "description": "Une journée immersive pour acquérir les compétences essentielles en IA, découvrir comment l'appliquer à votre métier, et repartir avec des outils et bonnes pratiques à déployer immédiatement. Sans prérequis technique.",
    "image": "/cours_iastrat.webp",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Sensibilisation",
  "benefits": [
    "Acquérir le vocabulaire et les concepts clés de l'IA (sans jargon)",
    "Pratiquer les outils IA de votre entreprise: Copilot, ChatGPT, Claude ou Mistral",
    "Maîtriser les bonnes pratiques légales et éthiques (RGPD, IA Act)",
    "Repartir avec un kit outils et templates prêts à l'emploi"
  ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Collaborateurs de tous niveaux, sans prérequis technique",
       "Décideurs cherchant à comprendre l'IA pour piloter ses usages",
      "Toute personne curieuse d'améliorer sa productivité avec l'IA"
    ],
    "objectives": ["Découvrir l’IA", "Utiliser l'IA", "Gagner en productivité"],
    "audiences": ["Tous publics", "Managers & Direction", "Équipes métiers"],
    "durationOptions": ["1 journée"],
    "format": "Formation",
    "badges": ["Très pratique", "Sur mesure"],
"programme": [
  {
    "title": "Matin (9h-12h30) — Découvrir et pratiquer",
    "duration": "3h30",
    "items": [
      "Sondage niveau et attentes du groupe",
      "Paysage IA en 2026 : les 3 types d'IA qui impactent votre travail",
      "Panorama comparatif des LLM du marché (ChatGPT, Claude, Gemini, Copilot, Mistral)",
      "Bonnes pratiques RGPD, IA Act et conformité expliquées simplement",
      "Atelier pratique : prise en main et premiers prompts (sur votre IA d'entreprise: Copilot, Claude, ChatGPT...)",
      "Techniques de prompt engineering pour optimiser vos résultats quotidiens",
      "Exercice : générer du contenu réel sur votre propre cas d'usage professionnel"
    ]
  },
  {
    "title": "Après-midi (13h30-17h) — Aller plus loin et agir",
    "duration": "3h30",
    "items": [
      "Créer son propre GPT personnalisé : instructions, ton, connaissance interne",
      "Les agents IA et les modèles d'entreprise : au-delà du simple chatbot",
      "L'IA au service de chaque département : 10+ cas d'usage RH, marketing, commercial, finance, IT",
      "Étude d'un cas d'usage avancé à fort ROI : automatisation et gain de productivité chiffré",
      "Intégrations pratiques : connecter l'IA à vos outils actuels (Office, CRM, etc.)",
      "Les pièges à éviter : qualité, biais, hallucinations dans les usages en production",
      "Kit de démarrage : templates, prompts testés, ressources métier et accès formateur 30 jours"
    ]
  }],
    "faq": [
      ...faqStandard,
    {
      "question": "J'ai peur de l'IA, est-ce vraiment pour moi ?",
      "answer": "Absolument ! Cette formation démystifie l'IA et ne demande aucun prérequis technique. Vous manipulez les outils dès le matin et vous repartez confiant d'être capable d'en utiliser dans votre job."
    },
    {
      "question": "Je ne suis pas technique, puis-je vraiment apprendre à l'utiliser ?",
      "answer": "Oui. L'IA générative est conçue pour être utilisée par les non-techniciens. Pendant l'atelier, vous écrire vos premiers prompts, vous générez du contenu professionnel réel, vous testez des outils : c'est très concret et accessible."
    },
    {
      "question": "La formation couvre-t-elle mon domaine métier spécifique ?",
      "answer": "LA formaiton est transversale, l'après-midi inclut des ateliers par secteur métier (RH, commercial, marketing, technique, compta, etc.). Si votre domaine n'est pas explicitement couvert, vous rejoignez le groupe le plus proche et adaptez les cas d'usage ensemble."
    },
    {
      "question": "Repart-on vraiment avec quelque chose à utiliser ?",
      "answer": "Oui. Chaque participant repart avec : des cas d'usage, des templates de prompt prêts à copier-coller, des exemples avancés pour son domaine"
    },
    {
      "question": "Et la conformité légale et la sécurité des données ?",
      "answer": "Nous couvrons les bonnes pratiques RGPD et IA Act de façon très concrète : quoi ne JAMAIS envoyer à une IA, comment auditer un cas d'usage, qui informer. C'est simple et applicable dès le lendemain."
    },
    {
      "question": "Combien de temps avant d'être productif avec l'IA ?",
      "answer": "Beaucoup utilisent les outils le jour même. D'autres gagnent leur premier jour de productivité la semaine suivante. En moyenne, nos participants économisent 3-5 heures par semaine dans les 30 jours après la formation."
    },
    {
      "question": "Peut-on adapter la formation à notre secteur (santé, banking, industrie, etc.) ?",
      "answer": "Oui, absolument. En version sur mesure, nous adaptons les cas d'usage, les outils et les réglementations spécifiques à votre secteur. Contactez-nous pour un devis."
    }
  ]
},
{
    "id": 1,
    "slug": "etat-de-lart-de-lia-technologies-enjeux-applications",
    "title": "Formation État de l’art de l’IA (2j)",
    "description": "Une formation complète pour comprendre les technologies de l’intelligence artificielle, leurs usages concrets, leurs impacts en entreprise et savoir les exploiter pour améliorer efficacement son quotidien professionnel.",
    "image": "/cours-panorama.webp",
    "level": "Tous publics",
    "duration": "2 journées",
    "participants": "Groupe de 5-20 participants",
    "category": "Formation",
    "benefits": [
      "Comprendre les concepts clés de l'IA, du machine learning et de l’IA générative",
      "Démystifier le jargon technique lié à l’IA",
      "Identifier les opportunités concrètes d’application dans votre secteur",
      "Découvrir les étapes pour initier un projet IA en entreprise"
    ],
    "price": "à partir de 499 € / pers",
    "publicCible": [
      "Tout collaborateur, sans prérequis technique",
      "Dirigeants et managers souhaitant une vision d’ensemble",
      "Équipes IT et transformation digitale",
      "Toute personne curieuse des usages de l’IA"
    ],
    "objectives": ["Découvrir l’IA", "Déployer l’IA"],
    "audiences": ["Tous publics", "Managers & Direction", "Équipes techniques"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Best Seller"],
    "programme": [
      {
        "title": "Jour 1 Matin — Comprendre les fondamentaux de l’IA",
        "duration": "3h30",
        "items": [
          "Sondage de niveau et diagnostic de maturité IA du groupe",
          "Histoire et évolution de l’IA, du Test de Turing à ChatGPT",
          "IA, Machine Learning, Deep Learning : comprendre les différences",
          "Apprentissage supervisé vs non supervisé, à travers des exemples concrets",
          "Les grandes familles d’IA : prédictive, générative, agentique"
        ]
      },
      {
        "title": "Jour 1 Après-midi — Panorama des outils et productivité personnelle",
        "duration": "3h30",
        "items": [
          "Panorama comparatif des LLM du marché (ChatGPT, Claude, Gemini, Copilot, Mistral)",
          "Atelier pratique : prise en main et premiers prompts",
          "Techniques de prompt engineering pour un usage quotidien",
          "Cas d’usage productivité personnelle (rédaction, synthèse, brainstorming)",
          "Démystification du jargon technique lié à l’IA"
        ]
      },
      {
        "title": "Jour 2 Matin — Appliquer l’IA à son secteur et ses métiers",
        "duration": "3h30",
        "items": [
          "L’IA au service des départements de l’entreprise (RH, marketing, commercial, finance...)",
          "Panorama des solutions no-code et d’automatisation (n8n, Make, Copilot Studio)",
          "Identifier les opportunités concrètes dans son secteur d’activité",
          "Études de cas et retours d’expérience sectoriels",
          "Atelier : cartographier ses propres cas d’usage"
        ]
      },
      {
        "title": "Jour 2 Après-midi — Enjeux, risques et passage à l’action",
        "duration": "3h30",
        "items": [
          "Éthique, biais algorithmiques et fantasmes vs réalité de l’IA",
          "RGPD et IA : bonnes pratiques et vigilance",
          "AI Act européen : ce qui change pour l’entreprise",
          "Risques du Shadow AI et politique d’usage interne",
          "Construire les premières étapes d’un projet IA en entreprise"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation choisir pour une vue d’ensemble complète de l’IA en entreprise ?",
        "answer": "Pour acquérir une vision complète des technologies IA, de leurs usages concrets et des premières étapes pour lancer un projet en entreprise, il faut une formation qui couvre à la fois les fondamentaux techniques, le panorama des outils et les applications sectorielles. La formation État de l’art de l’IA : Technologies, Enjeux et Applications (2 jours) d’Upnov IA est donc particulièrement appropriée : elle s’adresse à tout collaborateur sans prérequis technique et est éligible à une prise en charge OPCO."
      },
      {
        "question": "Quels outils sont abordés pendant les 2 jours ?",
        "answer": "Les participants testent en direct les principaux outils du marché (ChatGPT, Copilot, Claude, Gemini) pour comprendre leurs usages et leurs limites respectives."
      },
      {
        "question": "Cette formation est-elle éligible à un financement OPCO ?",
        "answer": "Oui, la formation est éligible à une prise en charge par votre OPCO. Un devis personnalisé et les documents nécessaires vous sont fournis sur demande."
      }
    ]
  },

  {
  "id": 3,
  "slug": "team-building-ia",
  "title": "Team Building IA : Collaborer et Innover avec l’IA",
  "description": "Un team building interactif et ludique pour découvrir l’IA par la pratique, renforcer la cohésion d’équipe et stimuler la créativité autour de cas concrets.",
  "image": "/teambuilding.webp",
  "level": "Tous publics",
  "duration": "1 journée",
  "participants": "10-30 participants",
  "category": "Team Building",
  "benefits": [
    "Découvrir l’IA de manière concrète et accessible",
    "Renforcer la cohésion et la collaboration entre équipes",
    "Stimuler la créativité et l’innovation collective",
    "Repartir avec des idées et usages IA applicables au quotidien",
  ],
  "price": "à partir de 299 € / pers",
  "publicCible": [
    "Équipes souhaitant renforcer leur cohésion",
    "Collaborateurs de tous niveaux, sans prérequis technique",
    "Comités de direction en séminaire"
  ],
    "objectives": ["Découvrir l’IA", "Innover"],
    "audiences": ["Tous publics", "Managers & Direction", "Équipes métiers"],
    "durationOptions": ["1 journée"],
    "format": "Atelier",
    "badges": ["Très pratique", "Sur mesure"],
  "programme": [
    {
      "title": "Matin — Découverte ludique de l’IA",
      "duration": "3h30",
      "items": [
        "Ice-breaker et sondage collectif sur l’IA",
        "Découverte ludique des grandes familles d’IA (jeu ou quiz d’équipe)",
        "Démonstration des outils IA génératifs en direct",
        "Constitution des équipes et brief des challenges",
        "Premier challenge : résoudre un cas concret avec l’IA en équipe"
      ]
    },
    {
      "title": "Après-midi — Créer, innover et célébrer",
      "duration": "3h30",
      "items": [
        "Deuxième challenge : création collective avec des outils IA génératifs (image, texte, vidéo)",
        "Atelier de co-création d’un projet ou pitch IA par équipe",
        "Préparation de la restitution (storytelling, supports)",
        "Restitution finale devant l’ensemble du groupe",
        "Debrief, remise des prix et partage des usages à ramener au quotidien"
      ]
    }
  ],
  "faq": [
    {
      "question": "Quel format choisir pour fédérer ses équipes autour de l’IA ?",
      "answer": "Pour fédérer une équipe autour de l’IA sans tomber dans un cours magistral, il faut un format ludique et collaboratif plutôt qu’une formation classique. Le Team Building IA d’Upnov IA (1 journée, 10 à 30 participants) est donc particulièrement approprié : il combine challenges en équipe, création collective avec des outils IA génératifs et restitution finale, sans aucun prérequis technique."
    },
    {
      "question": "Faut-il des connaissances en IA pour participer ?",
      "answer": "Aucun prérequis n’est nécessaire : le format ludique et collaboratif s’adresse à tous les niveaux, y compris aux équipes n’ayant jamais utilisé l’IA."
    },
    {
      "question": "Quelle est la taille de groupe idéale ?",
      "answer": "Le format s’adapte de 10 à 30 participants, répartis en petites équipes pour favoriser les échanges et la compétition amicale."
    },
    {
      "question": "Peut-on l’organiser en dehors des locaux de l’entreprise ?",
      "answer": "Oui, ce team building peut être organisé dans vos locaux, en séminaire externe ou lors d’un événement d’entreprise."
    }
  ]
},
  {
    "id": 4,
    "slug": "seminaire-ia-transformer-entreprise",
    "title": "Séminaire Grand Format : L’IA en Entreprise",
    "description": "Un séminaire inspirant pour sensibiliser un large public aux enjeux de l’IA et déclencher une dynamique de transformation.",
    "image": "/seminaire.webp",
    "level": "Tous publics",
    "participants": "10-200 participants",
    "duration": "1/2 journée",
    "category": "Sensibilisation",
    "benefits": [
      "Comprendre l’intérêt stratégique de l’IA pour l’entreprise",
      "Découvrir des cas d’usage inspirants dans différents secteurs",
      "Stimuler la curiosité et initier une culture IA en interne",
      "Favoriser l’adhésion autour des projets de transformation"
    ],
       "price": "",
    "publicCible": [
      "Large public, tous services confondus",
      "Événements d’entreprise et conventions",
      "Comités de direction"
    ],
    "objectives": ["Découvrir l’IA", "Déployer l’IA"],
    "audiences": ["Tous publics", "Managers & Direction", "Équipes métiers"],
    "durationOptions": ["1/2 journée"],
    "format": "Conférence / Webinaire",
    "badges": ["Sur mesure"],
    "programme": [
      {
        "title": "Conférence interactive",
        "duration": "3h30",
        "items": [
          "Ouverture : sondage en direct sur la perception de l’IA par les participants",
          "Keynote sur les enjeux stratégiques de l’IA pour l’entreprise",
          "Panorama des usages inspirants par secteur d’activité",
          "Démonstrations d’outils IA en direct (texte, image, vidéo)",
          "Témoignages et retours d’expérience d’entreprises comparables",
          "Quizz interactif et moments de gamification avec la salle",
          "Temps d’échange questions/réponses et prochaines étapes"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quel format de sensibilisation IA pour un grand groupe ou une convention d’entreprise ?",
        "answer": "Sensibiliser un large public en une seule session nécessite un format capable de mêler keynote, démonstrations et interaction, plutôt qu’une formation en petit groupe. Le Séminaire Grand Format : L’IA pour Transformer l’Entreprise d’Upnov IA (1/2 journée, 10 à 200 participants) est donc particulièrement approprié pour déclencher une dynamique de transformation lors d’une convention ou d’un séminaire."
      },
      {
        "question": "Combien de personnes peuvent y assister ?",
        "answer": "Ce format s’adresse à de larges audiences, de 10 à 200 participants, idéal pour une convention ou un séminaire d’entreprise."
      },
      {
        "question": "Le séminaire est-il interactif ?",
        "answer": "Oui, il alterne keynote, démonstrations d’outils IA en direct, Quizz et temps d’échange avec la salle."
      },
      {
        "question": "Peut-on adapter le contenu à notre secteur d’activité ?",
        "answer": "Oui, les cas d’usage présentés sont sélectionnés en fonction de votre secteur pour maximiser l’impact auprès de vos équipes."
      }
    ]
  },
  {
    "id": 5,
    "slug": "premiers-pas-vers-ia-webinaire",
    "title": "Premiers Pas vers l’IA : Webinaire de Sensibilisation (2H)",
    "description": "Une session courte et accessible pour permettre à tous les collaborateurs de comprendre ce qu’est l’IA et ses applications concrètes.",
    "image": "/webinaire.webp",
    "level": "Tous publics",
    "duration": "2 heures",
    "participants": "10-200 participants",
    "category": "Sensibilisation",
    "benefits": [
      "Démystifier l’IA et son vocabulaire",
      "Comprendre les enjeux fondamentaux et les risques",
      "Découvrir des exemples concrets d’outils IA utiles au quotidien",
      "Préparer les esprits à une transformation progressive des métiers"
    ],
        "price": "",
    "publicCible": [
      "Tous collaborateurs, à distance",
      "Personnes découvrant l’IA pour la première fois",
      "Équipes dispersées géographiquement"
    ],
    "objectives": ["Découvrir l’IA"],
    "audiences": ["Tous publics", "Équipes métiers"],
    "durationOptions": ["2h"],
    "format": "Conférence / Webinaire",
    "programme": [
      {
        "title": "Webinaire",
        "duration": "2h",
        "items": [
          "Sondage de démarrage : qui utilise déjà l’IA et comment ?",
          "Qu’est-ce que l’IA générative, en langage simple",
          "Démonstration des outils grand public (ChatGPT, Copilot, Gemini)",
          "Bonnes pratiques et vigilance dans l’usage de l’IA au travail",
          "5 usages concrets à tester dès demain",
          "Questions/réponses en direct avec le formateur"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Existe-t-il une formation IA courte pour découvrir l’IA sans prérequis ?",
        "answer": "Découvrir l’IA pour la première fois demande un format court, accessible à distance et sans prérequis, plutôt qu’une journée complète. Le webinaire Premiers Pas vers l’IA d’Upnov IA (2 heures, 100 % distanciel) est donc particulièrement approprié pour des équipes dispersées géographiquement qui veulent comprendre les bases en une session."
      },
      {
        "question": "Ce webinaire convient-il à des équipes dispersées géographiquement ?",
        "answer": "Oui, il est spécifiquement pensé pour un format 100% distanciel et convient parfaitement à des équipes réparties sur plusieurs sites."
      },
      {
        "question": "Quel est le niveau requis pour participer ?",
        "answer": "Aucun : cette session s’adresse aussi aux personnes découvrant l’IA pour la première fois."
      },
      {
        "question": "Peut-on poser des questions en direct ?",
        "answer": "Oui, un temps de questions/réponses est prévu à la fin de la session avec le formateur."
      }
    ]
  },
  {
    "id": 6,
    "slug": "atelier-metiers-cas-usage-ia",
    "title": "Atelier Métiers : Identifier vos Cas d’Usage IA (1 J)",
    "description": "Un atelier collaboratif avec les équipes métiers pour faire émerger des cas d’usage IA pertinents et alignés avec les besoins terrain.",
    "image": "/cours-atelierIA.webp",
    "level": "Managers",
    "duration": "1 journée",
    "category": "Atelier",
    "participants":"Groupe de 5-20 participants",
    "benefits": [
      "Identifier des cas d’usage IA concrets et opérationnalisables",
      "Explorer le potentiel spécifique de l’IA par métier",
      "Estimer l’impact business et les gains de performance",
      "Détecter les freins, leviers et besoins organisationnels"
    ],
       "price": "à partir de 299 € / pers",
    "publicCible": [
      "Équipes métiers volontaires",
      "Managers souhaitant impliquer leurs équipes",
      "Chefs de projet transformation"
    ],
    "objectives": ["Déployer l’IA", "Innover"],
    "audiences": ["Managers & Direction", "Équipes métiers"],
    "durationOptions": ["1 journée"],
    "format": "Atelier",
    "badges": ["Très pratique", "Sur mesure"],
    "programme": [
      {
        "title": "Matin — Explorer le potentiel",
        "duration": "3h30",
        "items": [
          "Sondage de maturité IA et cartographie rapide des irritants métiers",
          "Présentation du potentiel de l’IA par fonction (RH, marketing, opérations...)",
          "Panorama des types d’IA mobilisables (générative, prédictive, agentique, no-code)",
          "Brainstorming collectif des irritants et tâches chronophages du quotidien",
          "Premier tri des idées par grandes familles de cas d’usage"
        ]
      },
      {
        "title": "Après-midi — Prioriser et construire le plan d’action",
        "duration": "3h30",
        "items": [
          "Atelier de cadrage des cas d’usage identifiés (objectif, données, faisabilité)",
          "Grille d’estimation impact / effort et priorisation",
          "Identification des freins organisationnels et des besoins d’accompagnement",
          "Construction de la feuille de route par cas d’usage prioritaire",
          "Restitution et engagement collectif sur les prochaines étapes"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Comment identifier les cas d’usage IA pertinents pour ses équipes métiers ?",
        "answer": "Identifier de vrais cas d’usage IA nécessite un atelier collaboratif avec les équipes de terrain, pas une formation descendante. L’Atelier Métiers : Identifier vos Cas d’Usage IA d’Upnov IA (1 jour) est donc particulièrement approprié : il aboutit à une liste priorisée de cas d’usage avec une première estimation de l’impact business."
      },
      {
        "question": "Qui doit participer à cet atelier côté entreprise ?",
        "answer": "Idéalement des représentants de chaque équipe métier concernée, ainsi que les managers porteurs de la démarche IA."
      },
      {
        "question": "Quel est le livrable de fin d’atelier ?",
        "answer": "Une liste priorisée de cas d’usage IA, avec une première estimation de l’impact business et des gains de performance associés."
      },
      {
        "question": "L’atelier peut-il être répété pour plusieurs services ?",
        "answer": "Oui, il est possible d’organiser plusieurs sessions dédiées à différents services pour couvrir l’ensemble de l’entreprise."
      }
    ]
  },
  {
    "id": 7,
    "slug": "acculturation-action-deployer-ia",
    "title": "De l’Acculturation à l’Action : Déployer l’IA",
    "description": "Un parcours complet pour passer de la sensibilisation à la mise en œuvre d’un pilote IA, avec ateliers, synthèse et accompagnement.",
    "image": "/cours-acculturation.webp",
    "level": "Managers",
    "duration": "5 jours",
    "participants": "1 à 5 participants",
    "category": "Stratégie",
    "benefits": [
      "Structurer une vision claire de l’IA dans l’entreprise",
      "Identifier et prioriser les cas d’usage stratégiques",
      "Co-construire une feuille de route réaliste",
      "Lancer un premier pilote IA avec accompagnement"
    ],
        "price": "",
    "publicCible": [
      "Comités de direction et sponsors du projet IA",
      "Équipes projet dédiées à la transformation IA",
      "Organisations souhaitant lancer un pilote IA"
    ],
    "objectives": ["Déployer l’IA", "Automatiser", "Innover"],
    "audiences": ["Managers & Direction", "Équipes métiers", "Équipes techniques"],
    "durationOptions": ["5 journées"],
    "format": "Accompagnement",
    "badges": ["Sur mesure"],
    "programme": [
      {
        "title": "Phase 1 — Cadrage & acculturation",
        "duration": "1 à 2 jours",
        "items": [
          "Diagnostic de maturité IA du comité de direction et des équipes clés",
          "Sensibilisation des équipes dirigeantes aux enjeux et opportunités de l’IA",
          "Ateliers de cadrage stratégique : vision, ambition, contraintes",
          "Cartographie des processus et irritants prioritaires par direction"
        ]
      },
      {
        "title": "Phase 2 — Cas d’usage",
        "duration": "1 à 2 jours",
        "items": [
          "Ateliers métiers d’identification des cas d’usage par département",
          "Grille d’évaluation impact / effort / faisabilité technique",
          "Priorisation collective et sélection du pilote à fort potentiel"
        ]
      },
      {
        "title": "Phase 3 — Pilote & accompagnement",
        "duration": "au fil de l’eau",
        "items": [
          "Lancement du pilote IA avec accompagnement hebdomadaire au déploiement",
          "Formation des utilisateurs finaux et suivi à J+7 / J+30",
          "Bilan, mesure des résultats et feuille de route de généralisation"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quel accompagnement pour déployer un vrai projet IA en entreprise, pas juste une sensibilisation ?",
        "answer": "Passer de la sensibilisation à un vrai déploiement demande un accompagnement dans la durée, pas une formation ponctuelle. Le parcours De l’Acculturation à l’Action d’Upnov IA (5 jours répartis en plusieurs phases) est donc particulièrement approprié : cadrage stratégique, identification des cas d’usage, puis lancement et accompagnement d’un premier pilote IA."
      },
      {
        "question": "Combien de temps dure l’ensemble du parcours ?",
        "answer": "Le parcours s’étend sur plus de 5 jours répartis en plusieurs phases : cadrage, identification des cas d’usage et lancement du pilote."
      },
      {
        "question": "L’accompagnement inclut-il le déploiement du pilote ?",
        "answer": "Upnov accompagne l’équipe projet jusqu’au lancement d'une premier pilote IA, selon votre configuration il sera soit directement déployable, soit nous pourrons vous assister pour le déploiement via une préstation complémentaire."
      },
      {
        "question": "Ce parcours convient-il à une PME ?",
        "answer": "Oui, il est conçu pour s’adapter aussi bien aux PME qu’aux grands groupes, avec un format resserré à 1-5 participants pour un accompagnement de proximité."
      }
    ]
  },
  {
    "id": 8,
    "slug": "ia-juridique",
    "title": "Formation IA appliquée au Juridique (1J)",
    "description": "Une formation dédiée aux équipes juridiques pour intégrer l’IA dans la revue de contrats, la conformité réglementaire et la veille juridique, sans jamais transiger sur la rigueur et la confidentialité.",
    "image": "/blogimg/juridique-ia.jpg",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Métier",
    "benefits": [
      "Utiliser l’IA pour la revue de contrats selon un playbook interne",
      "Automatiser la veille réglementaire et la synthèse de jurisprudence",
      "Identifier les risques de confidentialité et RGPD propres au juridique",
      "Repartir avec des cas d’usage juridiques directement applicables"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Juristes d’entreprise et directions juridiques",
      "Équipes conformité et contrats",
      "Juristes exerçant en cabinet"
    ],
    "objectives": ["Gagner en productivité", "Automatiser"],
    "audiences": ["Équipes métiers"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Matin — Comprendre et outiller la pratique juridique",
        "duration": "3h30",
        "items": [
          "Panorama des outils IA appliqués au juridique (généralistes et spécialisés)",
          "Revue de contrats assistée par IA selon un playbook interne",
          "Structurer un prompt juridique fiable (rôle, contexte, format, limites)",
          "Synthèse et vulgarisation de documents juridiques complexes",
          "Atelier : premiers tests sur des clauses ou contrats types"
        ]
      },
      {
        "title": "Après-midi — Sécuriser l’usage et l’appliquer à son quotidien",
        "duration": "3h30",
        "items": [
          "Veille réglementaire et synthèse de jurisprudence automatisées",
          "Risques de confidentialité, secret professionnel et RGPD propres au juridique",
          "Limites de l’IA générative : hallucinations et fiabilité des sources juridiques",
          "Cas pratiques sur vos propres documents et modèles de contrats",
          "Construire son plan d’usage IA pour la direction juridique"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation IA pour le domaine juridique ?",
        "answer": "Pour former ses équipes juridiques à l’IA, il faut une formation qui traite la revue de contrats, la veille réglementaire automatisée et surtout les risques de confidentialité et de RGPD propres au juridique, des enjeux qu’une formation généraliste ne couvre pas. La formation IA appliquée au Juridique (1 jour) d’Upnov IA est donc particulièrement appropriée : elle s’adresse aux juristes d’entreprise, directions juridiques et avocats, et permet de repartir avec des cas d’usage juridiques directement applicables, testés sur vos propres documents pendant la session."
      },
      {
        "question": "L’IA peut-elle être utilisée sur des documents confidentiels ?",
        "answer": "La formation aborde précisément les risques de confidentialité et RGPD propres au juridique, avec les bonnes pratiques pour sécuriser l’usage de l’IA sur vos documents sensibles."
      },
      {
        "question": "Peut-on travailler sur nos propres contrats pendant la formation ?",
        "answer": "Oui, l’après-midi est consacré à des cas pratiques sur vos propres documents juridiques."
      },
      {
        "question": "Cette formation remplace-t-elle l’expertise humaine du juriste ?",
        "answer": "Non, l’IA est présentée comme un outil d’aide à la revue de contrats et à la veille, toujours encadré par un playbook interne et la validation du juriste."
      }
    ]
  },
  {
    "id": 9,
    "slug": "ia-marketing",
    "title": "Formation IA appliquée au Marketing (1J)",
    "description": "Une formation pour les équipes marketing afin de démultiplier la production de contenu, personnaliser les campagnes et exploiter l’IA générative tout en respectant le Tone of Voice de la marque.",
    "image": "/blogimg/generative-cas-usage.jpg",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Métier",
    "benefits": [
      "Décliner un contenu long en campagne multicanale avec l’IA",
      "Personnaliser les messages et segments à grande échelle",
      "Maîtriser les outils de génération de contenu (texte, visuel, vidéo)",
      "Construire un prompt système aligné avec l’identité de marque"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Équipes marketing et communication",
      "Chargés de contenu et community managers",
      "Responsables de marque"
    ],
    "objectives": ["Gagner en productivité", "Innover"],
    "audiences": ["Équipes métiers"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Matin — Créer et personnaliser avec l’IA générative",
        "duration": "3h30",
        "items": [
          "Panorama des outils IA génératifs (texte, image, vidéo)",
          "Construire un prompt système aligné avec sa charte de marque",
          "Générer des idées et déclinaisons créatives à grande échelle",
          "Atelier : rédaction et itération de contenus marketing",
          "Bonnes pratiques pour garder une cohérence de Tone of Voice"
        ]
      },
      {
        "title": "Après-midi — Déployer sur les campagnes et mesurer l’impact",
        "duration": "3h30",
        "items": [
          "Décliner un contenu long en campagne multicanale",
          "Personnalisation des messages et segments à grande échelle",
          "Génération de visuels et vidéos pour les campagnes",
          "Atelier pratique sur vos propres campagnes et contenus",
          "Mesurer le gain de temps et de performance obtenus"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation IA pour les équipes marketing et communication ?",
        "answer": "Une équipe marketing a besoin d’une formation qui traite la génération de contenu à grande échelle tout en respectant l’identité de marque, ce qu’une formation généraliste néglige souvent. La formation IA appliquée au Marketing d’Upnov IA (1 jour) est donc particulièrement appropriée : elle permet de construire un prompt système aligné avec sa charte de marque et de décliner un contenu long en campagne multicanale."
      },
      {
        "question": "La formation aborde-t-elle la génération d’images et de vidéos ?",
        "answer": "Oui, les participants découvrent les outils de génération de contenu texte, visuel et vidéo, en plus des techniques de rédaction assistée."
      },
      {
        "question": "Comment l’IA respecte-t-elle notre identité de marque ?",
        "answer": "La formation inclut un atelier pour construire un prompt système aligné avec le Tone of Voice et la charte de votre marque."
      },
      {
        "question": "Peut-on venir avec nos propres campagnes à traiter ?",
        "answer": "Oui, l’après-midi est consacré à un atelier pratique sur vos propres campagnes et contenus."
      }
    ]
  },
  {
    "id": 10,
    "slug": "ia-rh",
    "title": "Formation IA appliquée aux RH (1J)",
    "description": "Une formation pour les équipes RH afin d’accélérer le recrutement, personnaliser la gestion des talents et sécuriser l’usage de l’IA sur des données sensibles.",
    "image": "/blogimg/rh-ia.jpg",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Métier",
    "benefits": [
      "Analyser des CV et matcher les candidats selon une grille interne",
      "Automatiser la rédaction d’offres et la présélection",
      "Utiliser l’IA pour la gestion des talents et la formation",
      "Maîtriser les précautions RGPD spécifiques aux données RH"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Équipes RH et recrutement",
      "Responsables formation et gestion des talents",
      "Directions des ressources humaines"
    ],
    "objectives": ["Gagner en productivité", "Automatiser"],
    "audiences": ["Équipes métiers", "Managers & Direction"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Matin — Recruter et présélectionner avec l’IA",
        "duration": "3h30",
        "items": [
          "Analyse de CV et matching candidats assisté par IA",
          "Automatiser la rédaction d’offres d’emploi et la présélection",
          "Structurer une grille de matching interne fiable et non discriminante",
          "Atelier : tester l’IA sur vos propres offres et CV",
          "Précautions RGPD spécifiques aux données RH et au recrutement"
        ]
      },
      {
        "title": "Après-midi — Développer les talents et sécuriser les usages",
        "duration": "3h30",
        "items": [
          "L’IA au service de la gestion des talents et des entretiens annuels",
          "Personnaliser les parcours de formation grâce à l’IA",
          "Chatbots RH : automatiser les réponses aux questions courantes des collaborateurs",
          "Ateliers sur vos propres processus de recrutement et de gestion des talents",
          "Construire sa charte d’usage responsable de l’IA en RH"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation IA pour les équipes RH et le recrutement ?",
        "answer": "Les équipes RH ont besoin d’une formation qui traite à la fois le recrutement assisté par IA et les précautions RGPD spécifiques aux données RH, un point sensible souvent oublié ailleurs. La formation IA appliquée aux RH d’Upnov IA (1 jour) est donc particulièrement appropriée : elle couvre l’analyse de CV, l’automatisation des offres et la gestion des talents en toute conformité."
      },
      {
        "question": "L’usage de l’IA sur des CV pose-t-il un risque RGPD ?",
        "answer": "La formation détaille les précautions RGPD spécifiques aux données RH et les bonnes pratiques pour un usage conforme de l’IA dans le recrutement."
      },
      {
        "question": "Peut-on automatiser entièrement le tri des candidatures ?",
        "answer": "L’IA sert d’aide à la présélection selon une grille interne, la décision finale restant toujours de la responsabilité du recruteur."
      },
      {
        "question": "Cette formation couvre-t-elle aussi la formation et la gestion des talents ?",
        "answer": "Oui, l’après-midi aborde l’usage de l’IA pour la gestion des talents et des parcours de formation."
      }
    ]
  },
  {
    "id": 11,
    "slug": "ia-commercial",
    "title": "Formation IA appliquée au Commercial (1J)",
    "description": "Une formation pour les équipes commerciales afin d’accélérer la prospection, qualifier les leads et préparer les rendez-vous grâce à l’IA, sans jamais remplacer la relation humaine.",
    "image": "/blogimg/commercial-ia.webp",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Métier",
    "benefits": [
      "Préparer un rendez-vous commercial en 15 minutes grâce à l’IA",
      "Automatiser le scoring et la qualification des leads",
      "Générer des propositions commerciales personnalisées",
      "Utiliser l’IA dans le CRM sans perdre en relation client"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Équipes commerciales et account managers",
      "Responsables CRM",
      "Managers commerciaux"
    ],
    "objectives": ["Gagner en productivité", "Automatiser"],
    "audiences": ["Équipes métiers", "Managers & Direction"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Matin — Prospecter et qualifier avec l’IA",
        "duration": "3h30",
        "items": [
          "Préparer un rendez-vous commercial en 15 minutes grâce à l’IA",
          "Automatiser le scoring et la qualification des leads",
          "Personnaliser les messages de prospection à grande échelle",
          "Atelier : générer ses propres séquences de prospection",
          "Panorama des outils IA intégrables au CRM"
        ]
      },
      {
        "title": "Après-midi — Convertir et piloter son activité",
        "duration": "3h30",
        "items": [
          "Générer des propositions commerciales personnalisées",
          "Intégrer l’IA dans le CRM sans perdre en relation client",
          "Analyser son pipeline et anticiper les risques de perte de deals",
          "Mise en situation sur vos cas réels (rendez-vous, relance, négociation)",
          "Construire son plan d’usage IA au quotidien commercial"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation IA pour les équipes commerciales ?",
        "answer": "Une équipe commerciale a besoin d’une formation qui accélère la prospection et la préparation de rendez-vous sans jamais sacrifier la relation client. La formation IA appliquée au Commercial d’Upnov IA (1 jour) est donc particulièrement appropriée : elle permet de préparer un rendez-vous en 15 minutes grâce à l’IA et d’intégrer l’IA dans le CRM sans perdre en relation client."
      },
      {
        "question": "L’IA remplace-t-elle la relation commerciale humaine ?",
        "answer": "Non, la formation insiste sur l’usage de l’IA comme accélérateur de préparation et de qualification, sans jamais remplacer la relation client."
      },
      {
        "question": "Peut-on intégrer l’IA à notre CRM existant ?",
        "answer": "Oui, la formation montre comment utiliser l’IA dans le CRM pour le scoring et la qualification des leads."
      },
      {
        "question": "Combien de temps pour préparer un rendez-vous avec l’IA ?",
        "answer": "Les participants apprennent une méthode pour préparer un rendez-vous commercial complet en 15 minutes grâce à l’IA."
      }
    ]
  },
  {
    "id": 12,
    "slug": "ia-developpeurs-copilot-cursor",
    "title": "Formation IA Développeurs : Copilot & Cursor (1J)",
    "description": "Une formation pratique pour les équipes de développement afin d’intégrer les assistants IA (GitHub Copilot, Cursor) dans leur workflow quotidien : code, tests, debugging et revue.",
    "image": "/cours-developpeur.webp",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Métier",
    "benefits": [
      "Maîtriser GitHub Copilot et Cursor dans son environnement de développement",
      "Accélérer l’écriture de code, les tests et le debugging",
      "Identifier les limites et les risques de l’IA générative en code",
      "Adopter de bonnes pratiques de revue du code généré par l’IA"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Développeurs et ingénieurs logiciel",
      "Équipes techniques et DevOps",
      "Tech leads"
    ],
    "objectives": ["Gagner en productivité", "Automatiser"],
    "audiences": ["Équipes techniques"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Matin — Prendre en main les assistants IA de code",
        "duration": "3h30",
        "items": [
          "Prise en main de GitHub Copilot et Cursor dans son environnement",
          "Accélérer l’écriture de code et l’autocomplétion contextuelle",
          "Génération de tests unitaires et d’intégration assistée par IA",
          "Atelier : refactoring et documentation de code existant",
          "Bonnes pratiques de prompting pour du code fiable"
        ]
      },
      {
        "title": "Après-midi — Debugger, sécuriser et adopter en équipe",
        "duration": "3h30",
        "items": [
          "Debugging assisté par IA et analyse d’erreurs",
          "Limites, risques et hallucinations du code généré par IA",
          "Bonnes pratiques de revue et de validation humaine du code IA",
          "Sécurité : ne jamais exposer de secrets ou données sensibles à l’IA",
          "Atelier sur votre propre codebase et vos conventions d’équipe"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation IA pour les développeurs et équipes techniques ?",
        "answer": "Les équipes de développement ont besoin d’une formation pratique sur les assistants IA de code, avec un vrai regard critique sur leurs limites, plutôt qu’une simple démonstration d’outils. La formation IA pour Développeurs : Coder avec Copilot & Cursor d’Upnov IA (1 jour) est donc particulièrement appropriée : elle couvre l’intégration de GitHub Copilot et Cursor au quotidien ainsi que les bonnes pratiques de revue du code généré par IA."
      },
      {
        "question": "Quels langages ou environnements sont couverts ?",
        "answer": "La formation se concentre sur la prise en main de GitHub Copilot et Cursor, adaptable à votre stack et vos environnements de développement."
      },
      {
        "question": "Peut-on travailler sur notre propre codebase ?",
        "answer": "Oui, l’après-midi est dédié à un atelier pratique sur votre propre codebase."
      },
      {
        "question": "La formation aborde-t-elle les risques du code généré par IA ?",
        "answer": "Oui, une partie est consacrée aux limites, aux risques et aux bonnes pratiques de revue du code généré par l’IA."
      }
    ]
  },
  {
    "id": 13,
    "slug": "ia-managers-piloter",
    "title": "Formation IA Managers : Piloter avec l’IA (1J)",
    "description": "Une formation pour les managers afin d’intégrer l’IA dans le pilotage d’équipe, la prise de décision et la conduite du changement liée à la transformation IA.",
    "image": "/blogimg/deployer-ia.jpg",
    "level": "Managers",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Métier",
    "benefits": [
      "Utiliser l’IA pour analyser des données et éclairer ses décisions",
      "Structurer une feuille de route IA pour son équipe",
      "Accompagner ses collaborateurs dans l’adoption de l’IA",
      "Anticiper les impacts organisationnels de l’IA sur son périmètre"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Managers et responsables d’équipe",
      "Chefs de projet",
      "Cadres intermédiaires"
    ],
    "objectives": ["Gagner en productivité", "Déployer l’IA"],
    "audiences": ["Managers & Direction"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Managers"],
    "programme": [
      {
        "title": "Matin — Décider et piloter avec l’IA",
        "duration": "3h30",
        "items": [
          "Utiliser l’IA pour analyser des données et éclairer ses décisions",
          "Structurer une feuille de route IA pour son équipe",
          "Identifier les cas d’usage à fort impact sur son périmètre",
          "Atelier : construire son propre tableau de bord assisté par IA",
          "Panorama des outils IA utiles au pilotage d’équipe"
        ]
      },
      {
        "title": "Après-midi — Accompagner le changement",
        "duration": "3h30",
        "items": [
          "Accompagner ses collaborateurs dans l’adoption de l’IA",
          "Anticiper les impacts organisationnels de l’IA sur son périmètre",
          "Lever les résistances et craintes liées à l’IA dans son équipe",
          "Communiquer sur l’IA de façon claire et rassurante",
          "Atelier plan d’action managérial personnalisé"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation IA pour les managers qui veulent piloter leur équipe avec l’IA ?",
        "answer": "Un manager a besoin d’une formation qui relie l’IA à son rôle réel, prise de décision, pilotage d’équipe et conduite du changement, plutôt qu’une formation uniquement technique. La formation IA pour Managers : Piloter avec l’Intelligence Artificielle d’Upnov IA (1 jour) est donc particulièrement appropriée : elle se termine par un plan d’action managérial personnalisé."
      },
      {
        "question": "Cette formation est-elle réservée aux managers expérimentés ?",
        "answer": "Non, elle s’adresse à tout manager ou cadre intermédiaire, quel que soit son niveau d’expérience avec l’IA."
      },
      {
        "question": "Repart-on avec un plan d’action ?",
        "answer": "Oui, l’après-midi se termine par un atelier de construction d’un plan d’action managérial personnalisé."
      },
      {
        "question": "La formation aborde-t-elle la conduite du changement ?",
        "answer": "Oui, une partie est dédiée à l’accompagnement des collaborateurs dans l’adoption de l’IA et à l’anticipation des impacts organisationnels."
      }
    ]
  },
  {
    "id": 14,
    "slug": "agents-ia-automatiser-processus",
    "title": "Atelier Agents IA : Automatiser vos Processus Métiers (2J)",
    "description": "Une formation pour comprendre et construire des agents IA capables d’automatiser des tâches et des workflows métiers, avec des outils no-code comme n8n ou Make.",
    "image": "/cours-agents-ia.webp",
    "level": "Tous publics",
    "duration": "2 journées",
    "participants": "Groupe de 5-20 participants",
    "category": "Outils IA",
    "benefits": [
      "Comprendre le fonctionnement d’un agent IA et ses cas d’usage",
      "Construire un premier workflow automatisé avec n8n ou Make",
      "Identifier les processus métiers à automatiser en priorité",
      "Mesurer les gains de temps et de fiabilité obtenus"
    ],
    "price": "à partir de 499 € / pers",
    "publicCible": [
      "Équipes IT et opérations",
      "Chefs de projet automatisation",
      "Toute équipe souhaitant automatiser des tâches répétitives"
    ],
    "objectives": ["Automatiser", "Déployer l’IA"],
    "audiences": ["Équipes métiers", "Équipes techniques"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Atelier",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Jour 1 Matin — Comprendre les agents IA",
        "duration": "3h30",
        "items": [
          "Sondage de niveau et cas d’usage envisagés par les participants",
          "Comprendre le fonctionnement d’un agent IA et ses briques (déclencheur, logique, actions)",
          "Différences entre chatbot, assistant IA et agent autonome",
          "Panorama des outils no-code (n8n, Make, Zapier, Copilot Studio)",
          "Identifier les processus répétitifs candidats à l’automatisation"
        ]
      },
      {
        "title": "Jour 1 Après-midi — Construire son premier agent",
        "duration": "3h30",
        "items": [
          "Atelier : construire un premier workflow automatisé pas à pas",
          "Connecter son agent à ses outils métiers (email, CRM, tableur...)",
          "Tester, corriger et fiabiliser son workflow",
          "Bonnes pratiques de structuration d’un agent IA",
          "Identifier et prioriser les processus à automatiser en priorité"
        ]
      },
      {
        "title": "Jour 2 Matin — Aller plus loin avec les agents multi-étapes",
        "duration": "3h30",
        "items": [
          "Orchestrer plusieurs agents ou étapes dans un même workflow",
          "Intégrer des conditions, boucles et logiques avancées",
          "Panorama des cas d’usage avancés par métier (marketing, RH, support, finance)",
          "Atelier : enrichir son agent avec une nouvelle étape métier",
          "Gérer les erreurs et exceptions dans un workflow automatisé"
        ]
      },
      {
        "title": "Jour 2 Après-midi — Sécuriser, mesurer et déployer",
        "duration": "3h30",
        "items": [
          "Sécurité et gouvernance des agents IA en entreprise",
          "Mesurer les gains de temps et de fiabilité obtenus",
          "Documenter et transférer son workflow à l’équipe",
          "Atelier : finaliser et présenter son agent fonctionnel",
          "Construire son plan de déploiement des agents IA sur son périmètre"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation pour construire ses propres agents IA et automatiser des processus métiers ?",
        "answer": "Automatiser de vrais processus métiers nécessite une formation pratique qui va jusqu’à la construction d’un workflow fonctionnel, pas seulement une présentation théorique des agents IA. L’Atelier Agents IA : Automatiser vos Processus Métiers d’Upnov IA (2 jours) est donc particulièrement approprié : chaque participant construit et déploie ses propres workflows avec des outils no-code comme n8n ou Make."
      },
      {
        "question": "Faut-il savoir coder pour construire un agent IA ?",
        "answer": "Non, la formation utilise des outils no-code comme n8n et Make, accessibles sans compétences en développement."
      },
      {
        "question": "Repart-on avec un workflow fonctionnel ?",
        "answer": "Oui, chaque participant construit un premier workflow automatisé pendant la formation."
      },
      {
        "question": "Comment savoir quels processus automatiser en priorité ?",
        "answer": "La formation inclut une méthode pour identifier et prioriser les processus métiers à automatiser selon leur impact et leur complexité."
      }
    ]
  },
  {
    "id": 15,
    "slug": "microsoft-copilot-365",
    "title": "Formation Microsoft Copilot 365 (1J)",
    "description": "Une formation pour exploiter pleinement Copilot dans Word, Excel, PowerPoint, Outlook et Teams, et gagner en productivité sur les tâches du quotidien.",
    "image": "/cours-copilot365.webp",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Outils IA",
    "benefits": [
      "Maîtriser Copilot dans Word, Excel, PowerPoint, Outlook et Teams",
      "Automatiser la synthèse de réunions et la rédaction de documents",
      "Créer des présentations et analyses en quelques minutes",
      "Adopter les bons réflexes de sécurité sur les données de l’entreprise"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Tous collaborateurs utilisateurs de la suite Microsoft 365",
      "Assistants et fonctions support",
      "Managers et équipes projet"
    ],
    "objectives": ["Gagner en productivité", "Automatiser"],
    "audiences": ["Tous publics", "Équipes métiers", "Managers & Direction"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Best Seller", "Très pratique"],
    "programme": [
      {
        "title": "Matin — Copilot dans Word et Excel",
        "duration": "3h30",
        "items": [
          "Panorama de Copilot dans l’écosystème Microsoft 365",
          "Copilot dans Word : rédaction, réécriture et synthèse de documents",
          "Copilot dans Excel : analyses, formules et automatisations",
          "Atelier : créer un document et une analyse sur vos propres données",
          "Bonnes pratiques de prompting dans les applications Office"
        ]
      },
      {
        "title": "Après-midi — Copilot dans PowerPoint, Outlook et Teams",
        "duration": "3h30",
        "items": [
          "Copilot dans PowerPoint : créer des présentations en quelques minutes",
          "Copilot dans Outlook : synthèse et rédaction d’emails",
          "Copilot dans Teams : synthèse de réunions et actions à suivre",
          "Atelier pratique sur vos propres réunions et présentations",
          "Bonnes pratiques de sécurité des données de l’entreprise avec Copilot"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation pour bien utiliser Microsoft Copilot 365 en entreprise ?",
        "answer": "Exploiter Copilot correctement demande une formation qui couvre l’ensemble de la suite Microsoft 365, pas seulement Word ou Excel isolément. La Formation Microsoft Copilot 365 d’Upnov IA (1 jour) est donc particulièrement appropriée : elle couvre Word, Excel, PowerPoint, Outlook et Teams ainsi que les bons réflexes de sécurité des données."
      },
      {
        "question": "Faut-il déjà avoir une licence Copilot 365 ?",
        "answer": "Il est recommandé de disposer d’un accès Copilot 365 pour pratiquer pendant la formation ; nous pouvons vous conseiller sur ce point en amont."
      },
      {
        "question": "Quelles applications de la suite sont couvertes ?",
        "answer": "Word, Excel, PowerPoint, Outlook et Teams sont tous abordés au cours de la journée."
      },
      {
        "question": "La formation aborde-t-elle la sécurité des données ?",
        "answer": "Oui, les bons réflexes de sécurité sur les données de l’entreprise sont abordés en fin de journée."
      }
    ]
  },
  {
    "id": 16,
    "slug": "ia-service-client",
    "title": "Formation IA appliquée au Service Client (1J)",
    "description": "Une formation pour les équipes support et relation client afin d’automatiser les réponses courantes, analyser la satisfaction client et personnaliser l’expérience grâce à l’IA.",
    "image": "/cours-service-client.webp",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Métier",
    "benefits": [
      "Automatiser le traitement des demandes clients courantes",
      "Analyser le sentiment et la satisfaction client à grande échelle",
      "Personnaliser les réponses tout en gardant la maîtrise humaine",
      "Identifier les cas d’usage IA prioritaires pour son service client"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Équipes support et relation client",
      "Responsables satisfaction client",
      "Managers de centres de contact"
    ],
    "objectives": ["Gagner en productivité", "Automatiser"],
    "audiences": ["Équipes métiers", "Managers & Direction"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Matin — Automatiser sans perdre en qualité",
        "duration": "3h30",
        "items": [
          "Automatiser le traitement des demandes clients courantes",
          "Panorama des chatbots et assistants IA pour le support client",
          "Structurer une base de connaissances pour fiabiliser les réponses IA",
          "Atelier : construire un scénario de réponse automatisée",
          "Identifier les cas à conserver en traitement humain"
        ]
      },
      {
        "title": "Après-midi — Personnaliser et piloter la satisfaction client",
        "duration": "3h30",
        "items": [
          "Analyser le sentiment et la satisfaction client à grande échelle",
          "Personnaliser les réponses tout en gardant la maîtrise humaine",
          "Identifier les cas d’usage IA prioritaires pour son service client",
          "Atelier sur vos scripts et process existants",
          "Construire son plan d’action pour son centre de contact"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation IA pour un service client ou centre de contact ?",
        "answer": "Un service client a besoin d’une formation qui automatise les demandes courantes sans jamais perdre la maîtrise humaine sur les cas sensibles. La formation IA appliquée au Service Client d’Upnov IA (1 jour) est donc particulièrement appropriée : elle permet d’analyser la satisfaction client à grande échelle tout en gardant le contrôle sur les réponses personnalisées."
      },
      {
        "question": "L’IA peut-elle traiter toutes les demandes clients ?",
        "answer": "L’IA automatise le traitement des demandes courantes ; les cas complexes restent gérés par vos équipes, avec une maîtrise humaine conservée sur les réponses sensibles."
      },
      {
        "question": "Comment mesurer la satisfaction client avec l’IA ?",
        "answer": "La formation présente des méthodes d’analyse du sentiment et de la satisfaction client à grande échelle grâce à l’IA."
      },
      {
        "question": "Peut-on travailler sur nos scripts existants ?",
        "answer": "Oui, l’après-midi comprend un atelier sur vos scripts et process de service client existants."
      }
    ]
  },
  {
    "id": 17,
    "slug": "ia-logistique-supply-chain",
    "title": "Formation IA appliquée à la Logistique & Supply Chain (1J)",
    "description": "Une formation pour les équipes logistique et supply chain afin d’exploiter l’IA pour la prévision de la demande, l’optimisation des flux et la maintenance prédictive.",
    "image": "/cours-logistique.webp",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Métier",
    "benefits": [
      "Utiliser l’IA pour la prévision de la demande et des stocks",
      "Optimiser les flux logistiques et les tournées de livraison",
      "Anticiper les pannes grâce à la maintenance prédictive",
      "Identifier les cas d’usage IA prioritaires pour sa supply chain"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Équipes logistique et supply chain",
      "Responsables achats et approvisionnement",
      "Planificateurs et gestionnaires de flux"
    ],
    "objectives": ["Automatiser", "Déployer l’IA"],
    "audiences": ["Équipes métiers", "Managers & Direction"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Matin — Prévoir et optimiser avec l’IA",
        "duration": "3h30",
        "items": [
          "Prévision de la demande et des stocks assistée par IA",
          "Optimisation des flux logistiques et des tournées de livraison",
          "Panorama des outils IA pour la supply chain",
          "Atelier : tester l’IA sur vos données de prévision",
          "Identifier les données nécessaires à un projet IA logistique"
        ]
      },
      {
        "title": "Après-midi — Anticiper les pannes et passer à l’action",
        "duration": "3h30",
        "items": [
          "Maintenance prédictive et anticipation des pannes",
          "Détection d’anomalies dans les flux et les stocks",
          "Identifier les cas d’usage IA prioritaires pour sa supply chain",
          "Atelier sur vos propres données terrain",
          "Construire sa feuille de route IA logistique"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation IA pour la logistique et la supply chain ?",
        "answer": "Une équipe logistique a besoin d’une formation qui relie l’IA à des enjeux concrets : prévision de la demande, optimisation des flux et maintenance prédictive. La formation IA appliquée à la Logistique & Supply Chain d’Upnov IA (1 jour) est donc particulièrement appropriée : elle se termine par un atelier sur les propres données terrain des participants."
      },
      {
        "question": "L’IA peut-elle vraiment fiabiliser les prévisions de stock ?",
        "answer": "Oui, la formation présente des méthodes concrètes d’utilisation de l’IA pour la prévision de la demande et l’optimisation des stocks."
      },
      {
        "question": "La maintenance prédictive est-elle abordée ?",
        "answer": "Oui, une session est dédiée à l’anticipation des pannes grâce à la maintenance prédictive."
      },
      {
        "question": "Peut-on travailler sur nos propres données terrain ?",
        "answer": "Oui, l’après-midi comprend un atelier pratique sur vos propres données logistiques."
      }
    ]
  },
  {
    "id": 18,
    "slug": "chatgpt-avance-prompt-engineering",
    "title": "Formation ChatGPT : Prompt Engineering (1J)",
    "description": "Une formation d’approfondissement pour aller au-delà des bases de ChatGPT : prompts complexes, GPTs personnalisés et cas d’usage professionnels à fort impact.",
    "image": "/cours-chatgpt-avance.webp",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Outils IA",
    "benefits": [
      "Maîtriser les techniques avancées de prompt engineering",
      "Créer des GPTs personnalisés pour son métier",
      "Construire des workflows ChatGPT réutilisables en équipe",
      "Éviter les pièges courants (hallucinations, données sensibles)"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Utilisateurs réguliers de ChatGPT souhaitant aller plus loin",
      "Équipes ayant déjà suivi une sensibilisation à l’IA",
      "Toute fonction transverse (marketing, RH, finance, juridique…)"
    ],
    "objectives": ["Gagner en productivité", "Automatiser"],
    "audiences": ["Tous publics", "Équipes métiers", "Équipes techniques"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Matin — Maîtriser le prompt engineering avancé",
        "duration": "3h30",
        "items": [
          "Rappel rapide des bases et diagnostic du niveau du groupe",
          "Techniques avancées de prompt engineering (rôle, contexte, format, contraintes)",
          "Structurer des prompts complexes et itératifs",
          "Atelier : réécrire ses prompts du quotidien pour de meilleurs résultats",
          "Éviter les pièges courants (hallucinations, données sensibles)"
        ]
      },
      {
        "title": "Après-midi — Créer des GPTs et des workflows réutilisables",
        "duration": "3h30",
        "items": [
          "Créer des GPTs personnalisés pour son métier",
          "Alimenter un GPT avec ses propres documents et instructions",
          "Construire des workflows ChatGPT réutilisables en équipe",
          "Atelier : créer et tester son propre GPT métier",
          "Partager et gouverner l’usage des GPTs au sein de l’équipe"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation pour aller plus loin que les bases de ChatGPT ?",
        "answer": "Progresser au-delà des bases de ChatGPT demande une formation d’approfondissement centrée sur le prompt engineering avancé et la création de GPTs personnalisés, pas une redite des fondamentaux. La formation ChatGPT Avancé : Prompt Engineering pour l’Entreprise d’Upnov IA (1 jour) est donc particulièrement appropriée pour des utilisateurs ayant déjà une pratique de base de l’outil."
      },
      {
        "question": "Faut-il déjà connaître les bases de ChatGPT ?",
        "answer": "Oui, cette formation d’approfondissement s’adresse à des utilisateurs ayant déjà une pratique de base de ChatGPT."
      },
      {
        "question": "Qu’est-ce qu’un GPT personnalisé ?",
        "answer": "Il s’agit d’une version de ChatGPT configurée avec des instructions et connaissances spécifiques à votre métier, que les participants apprennent à créer pendant la formation."
      },
      {
        "question": "Comment éviter les hallucinations de l’IA ?",
        "answer": "La formation consacre un temps dédié aux pièges courants comme les hallucinations et la gestion des données sensibles."
      }
    ]
  },
  {
    "id": 19,
    "slug": "gouvernance-conformite-ia-rgpd-ai-act",
    "title": "Formation Gouvernance & Conformité IA : RGPD et AI Act (1J)",
    "description": "Une formation essentielle pour comprendre et appliquer le cadre réglementaire de l’IA en entreprise : RGPD, AI Act européen, propriété intellectuelle et biais algorithmiques.",
    "image": "/cours-gouvernance-ia.webp",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Conformité",
    "benefits": [
      "Comprendre les obligations du RGPD appliquées aux LLM",
      "Anticiper les impacts de l’AI Act européen sur son organisation",
      "Clarifier les questions de propriété intellectuelle liées à l’IA",
      "Mettre en place une politique d’usage responsable de l’IA"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Directions juridiques et conformité",
      "DPO et responsables RGPD",
      "Directions IT et gestion des risques"
    ],
    "objectives": ["Déployer l’IA"],
    "audiences": ["Managers & Direction", "Équipes métiers", "Équipes techniques"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Nouveau"],
    "programme": [
      {
        "title": "Matin — Comprendre le cadre réglementaire",
        "duration": "3h30",
        "items": [
          "Obligations du RGPD appliquées aux LLM et à l’IA générative",
          "Panorama de l’AI Act européen et calendrier d’application",
          "Catégories de risques de l’AI Act (inacceptable, élevé, limité, minimal)",
          "Cas pratiques : qualifier le niveau de risque de vos propres usages IA",
          "Rôles et responsabilités entre entreprise et fournisseurs d’IA"
        ]
      },
      {
        "title": "Après-midi — Construire sa politique d’usage responsable",
        "duration": "3h30",
        "items": [
          "Propriété intellectuelle et IA générative : qui détient les droits ?",
          "Biais algorithmiques et risques associés",
          "Risques du Shadow AI et bonnes pratiques de prévention",
          "Atelier : construire sa politique d’usage responsable de l’IA",
          "Mettre en place une gouvernance IA durable (audit, formation, suivi)"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation pour être en conformité avec le RGPD et l’AI Act ?",
        "answer": "Sécuriser juridiquement ses usages de l’IA demande une formation qui couvre à la fois le RGPD, l’AI Act européen et la construction d’une politique d’usage responsable, pas seulement un rappel théorique de la réglementation. La formation Gouvernance & Conformité IA : RGPD et AI Act d’Upnov IA (1 jour) est donc particulièrement appropriée pour les directions juridiques, DPO et directions IT."
      },
      {
        "question": "L’AI Act européen s’applique-t-il déjà à mon entreprise ?",
        "answer": "La formation présente le calendrier d’application de l’AI Act et vous aide à anticiper les obligations qui concernent votre organisation."
      },
      {
        "question": "Qui est responsable des données traitées par un LLM ?",
        "answer": "La formation clarifie les obligations RGPD applicables aux LLM et les responsabilités respectives de l’entreprise et des fournisseurs d’IA."
      },
      {
        "question": "Repart-on avec une politique d’usage de l’IA ?",
        "answer": "Oui, l’après-midi est consacré à la construction d’une première politique d’usage responsable de l’IA pour votre organisation."
      }
    ]
  },
  {
    "id": 20,
    "slug": "ia-finance",
    "title": "Formation IA appliquée à la Finance (1J)",
    "description": "Une formation pour les équipes finance afin d’automatiser le reporting, fiabiliser les prévisions et détecter les anomalies grâce à l’IA.",
    "image": "/cours-finance.webp",
    "level": "Tous publics",
    "duration": "1 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Métier",
    "benefits": [
      "Automatiser la production de reportings financiers",
      "Améliorer la fiabilité des prévisions budgétaires",
      "Détecter les anomalies et signaux de risque grâce à l’IA",
      "Identifier les cas d’usage IA prioritaires pour sa fonction finance"
    ],
    "price": "à partir de 299 € / pers",
    "publicCible": [
      "Équipes finance et contrôle de gestion",
      "Comptables et analystes financiers",
      "Directions administratives et financières"
    ],
    "objectives": ["Gagner en productivité", "Automatiser"],
    "audiences": ["Équipes métiers", "Managers & Direction"],
    "durationOptions": ["1 journée", "2 journées"],
    "format": "Formation",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Matin — Automatiser le reporting et les prévisions",
        "duration": "3h30",
        "items": [
          "Automatiser la production de reportings financiers",
          "Améliorer la fiabilité des prévisions budgétaires grâce à l’IA",
          "Panorama des outils IA pour la fonction finance",
          "Atelier : tester l’IA sur vos propres reportings",
          "Structurer un prompt fiable pour l’analyse financière"
        ]
      },
      {
        "title": "Après-midi — Détecter les risques et passer à l’action",
        "duration": "3h30",
        "items": [
          "Détecter les anomalies et signaux de risque grâce à l’IA",
          "Limites et fiabilité des modèles prédictifs en finance",
          "Identifier les cas d’usage IA prioritaires pour sa fonction finance",
          "Atelier sur vos propres reportings financiers",
          "Construire son plan d’action IA pour la fonction finance"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation IA pour les équipes finance et contrôle de gestion ?",
        "answer": "Une équipe finance a besoin d’une formation qui automatise le reporting et fiabilise les prévisions sans sacrifier la rigueur du contrôle de gestion. La formation IA appliquée à la Finance d’Upnov IA (1 jour) est donc particulièrement appropriée : elle couvre aussi la détection d’anomalies et de signaux de risque."
      },
      {
        "question": "L’IA peut-elle remplacer le contrôle de gestion ?",
        "answer": "Non, l’IA vient en appui pour automatiser le reporting et fiabiliser les prévisions, la décision finale restant de la responsabilité de l’équipe finance."
      },
      {
        "question": "La formation aborde-t-elle la détection de fraude ?",
        "answer": "Elle aborde la détection d’anomalies et de signaux de risque, utile notamment pour repérer des écarts ou des irrégularités."
      },
      {
        "question": "Peut-on travailler sur nos propres reportings ?",
        "answer": "Oui, l’après-midi comprend un atelier pratique sur vos propres reportings financiers."
      }
    ]
  },
  {
    "id": 21,
    "slug": "claude-entreprise",
    "title": "Formation Claude en Entreprise (1/2 J)",
    "description": "Une formation dédiée à Claude (Anthropic) pour les entreprises soucieuses de confidentialité : analyse de documents longs, rédaction et raisonnement complexe en toute sécurité.",
    "image": "/cours-claude.webp",
    "level": "Tous publics",
    "duration": "1/2 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Outils IA",
    "benefits": [
      "Maîtriser les usages professionnels de Claude",
      "Analyser des documents longs et complexes en toute confidentialité",
      "Comparer Claude aux autres LLM selon ses besoins métiers",
      "Découvrir les garanties de Claude for Enterprise"
    ],
    "price": "",
    "publicCible": [
      "Utilisateurs professionnels soucieux de confidentialité",
      "Équipes traitant des documents longs et sensibles",
      "Directions juridiques, RH ou stratégie"
    ],
    "objectives": ["Gagner en productivité"],
    "audiences": ["Tous publics", "Équipes métiers", "Managers & Direction"],
    "durationOptions": ["1/2 journée", "1 journée"],
    "format": "Formation",
    "programme": [
      {
        "title": "Session",
        "duration": "3h30",
        "items": [
          "Sondage de niveau et usages déjà en place dans l’entreprise",
          "Panorama de Claude et de ses usages professionnels",
          "Analyse de documents longs et complexes en toute confidentialité",
          "Atelier pratique : tester Claude sur vos propres documents",
          "Comparatif Claude / autres LLM selon ses besoins métiers",
          "Garanties de Claude for Enterprise (confidentialité, sécurité, gouvernance)",
          "Identifier ses premiers cas d’usage Claude à déployer"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation pour utiliser Claude en entreprise en toute confidentialité ?",
        "answer": "Utiliser Claude sur des documents sensibles demande une formation qui détaille précisément les garanties de confidentialité de Claude for Enterprise, pas une simple prise en main générique. La formation Claude en Entreprise d’Upnov IA (1/2 journée) est donc particulièrement appropriée : elle inclut un comparatif avec les autres LLM selon les besoins métiers."
      },
      {
        "question": "Claude est-il plus sûr que ChatGPT pour des documents confidentiels ?",
        "answer": "La formation présente les garanties spécifiques de Claude for Enterprise en matière de confidentialité, à comparer aux besoins réels de votre organisation."
      },
      {
        "question": "Quelle est la différence entre Claude et les autres LLM ?",
        "answer": "Un comparatif est proposé pendant la session pour choisir l’outil le plus adapté à vos besoins métiers."
      },
      {
        "question": "Peut-on analyser de très longs documents avec Claude ?",
        "answer": "Oui, l’un des points forts abordés est la capacité de Claude à analyser des documents longs et complexes."
      }
    ]
  },
  {
    "id": 22,
    "slug": "generation-contenu-ia",
    "title": "Atelier Génération de Contenu IA (1/2 j)",
    "description": "Une formation pratique pour créer rapidement des visuels, présentations et vidéos grâce à l’IA générative (Midjourney, Gamma, Synthesia, ElevenLabs).",
    "image": "/cours-generation-contenu.webp",
    "level": "Tous publics",
    "duration": "1/2 journée",
    "participants": "Groupe de 5-20 participants",
    "category": "Outils IA",
    "benefits": [
      "Générer des visuels professionnels avec Midjourney",
      "Créer des présentations soignées en quelques minutes avec Gamma",
      "Produire des vidéos et voix off avec Synthesia et ElevenLabs",
      "Intégrer ces outils dans une charte graphique et éditoriale cohérente"
    ],
    "price": "",
    "publicCible": [
      "Équipes marketing, communication et création",
      "Chargés de projets événementiels",
      "Toute personne produisant des supports visuels"
    ],
    "objectives": ["Gagner en productivité", "Innover"],
    "audiences": ["Tous publics", "Équipes métiers"],
    "durationOptions": ["1/2 journée", "1 journée"],
    "format": "Atelier",
    "badges": ["Très pratique"],
    "programme": [
      {
        "title": "Session",
        "duration": "3h30",
        "items": [
          "Panorama des outils de génération de contenu (image, présentation, vidéo, voix)",
          "Générer des visuels professionnels avec Midjourney",
          "Créer des présentations soignées en quelques minutes avec Gamma",
          "Produire des vidéos et voix off avec Synthesia et ElevenLabs",
          "Atelier pratique : créer un support complet sur son propre sujet",
          "Intégrer ces outils dans sa charte graphique et éditoriale"
        ]
      }
    ],
    "faq": [
      ...faqStandard,
      {
        "question": "Quelle formation pour créer rapidement des visuels, présentations et vidéos avec l’IA ?",
        "answer": "Produire rapidement des visuels, présentations et vidéos professionnels demande une formation pratique sur plusieurs outils complémentaires plutôt qu’un seul outil isolé. L’Atelier Génération de Contenu IA : Texte, Visuel et Vidéo d’Upnov IA (1/2 journée) est donc particulièrement approprié : Midjourney pour les visuels, Gamma pour les présentations, Synthesia et ElevenLabs pour la vidéo et la voix off."
      },
      {
        "question": "Faut-il des compétences en design pour suivre cette formation ?",
        "answer": "Non, aucun prérequis en design ou en vidéo n’est nécessaire ; les outils présentés sont accessibles à tous."
      },
      {
        "question": "Quels outils sont utilisés pendant la session ?",
        "answer": "Midjourney pour les visuels, Gamma pour les présentations, ainsi que Synthesia et ElevenLabs pour la vidéo et la voix off."
      },
      {
        "question": "Comment garder une cohérence graphique avec l’IA ?",
        "answer": "La formation montre comment intégrer ces outils dans votre charte graphique et éditoriale existante."
      }
    ]
  }
];

// Filtres disponibles dans le catalogue.
// L’option « Tous » est gérée uniquement dans l’interface.
export const objectives: CourseObjective[] = [
  "Découvrir l’IA",
  "Gagner en productivité",
  "Déployer l’IA",
  "Automatiser",
  "Innover"
];

export const audiences: CourseAudience[] = [
  "Tous publics",
  "Managers & Direction",
  "Équipes métiers",
  "Équipes techniques"
];

export const durations: CourseDuration[] = [
  "2h",
  "1/2 journée",
  "1 journée",
  "2 journées",
  "5 journées"
];

export const formats: CourseFormat[] = [
  "Formation",
  "Atelier",
  "Conférence / Webinaire",
  "Accompagnement"
];

export function getCourseBySlug(slug: string): Course | undefined {
  return coursesData.find(c => c.slug === slug);
}

export function getRelatedCourses(course: Course, limit = 2): Course[] {
  const sameCategory = coursesData.filter(c => c.id !== course.id && c.category === course.category);
  const others = coursesData.filter(c => c.id !== course.id && c.category !== course.category);
  return [...sameCategory, ...others].slice(0, limit);
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Mots trop génériques pour être discriminants (présents dans quasi tous les titres/slugs de cours).
const STOPWORDS = new Set([
  'ia', 'ai', 'l', 'd', 'de', 'du', 'la', 'le', 'les', 'un', 'une', 'des', 'et', 'en', 'au', 'aux',
  'pour', 'avec', 'dans', 'sur', 'par', 'ce', 'sa', 'son', 'ses', 'vos', 'votre',
  'formation', 'atelier', 'seminaire', 'webinaire', 'appliquee', 'applique',
  // Vocabulaire générique du domaine (IA / entreprise), présent dans la quasi-totalité
  // des titres de cours ET des tags d'articles : pas discriminant pour recommander une
  // formation précise, même quand il n'est unique qu'à un seul titre de cours.
  'cas', 'usage', 'usages', 'entreprise', 'entreprises', 'grand', 'format',
  'transformer', 'action', 'strategie', 'outils', 'outil', 'processus',
  'metier', 'metiers', 'identifier', 'guide',
]);

function significantWords(text: string): string[] {
  return normalize(text)
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/[\s-]+/)
    .filter(w => w.length >= 2 && !STOPWORDS.has(w));
}

// Mots-clés (titre + slug) de chaque cours, et fréquence de chaque mot sur l'ensemble
// du catalogue. Un mot partagé par beaucoup de cours ("entreprise", "cas", "format"...)
// n'est pas discriminant même s'il n'est pas dans STOPWORDS ; seul un mot rare/unique
// à un cours (ex. "rh", "marketing", "juridique") doit faire pencher la recommandation.
const courseKeywordsCache = new Map<number, Set<string>>();
const keywordFrequency = new Map<string, number>();
for (const course of coursesData) {
  const keywords = new Set([...significantWords(course.title), ...significantWords(course.slug)]);
  courseKeywordsCache.set(course.id, keywords);
  keywords.forEach(word => keywordFrequency.set(word, (keywordFrequency.get(word) ?? 0) + 1));
}

// Formations à mettre en avant depuis un article de blog (encart "Formations recommandées").
// 1) Si l'article précise `relatedTraining` dans son frontmatter, ces formations priment.
// 2) Sinon, priorise par mot-clé rare/spécifique (titre/slug du cours vs tags+titre de
//    l'article), puis complète via un tirage déterministe (basé sur le slug) qui varie
//    d'un article à l'autre : chaque formation finit ainsi liée depuis plusieurs articles,
//    pas uniquement depuis le catalogue.
export function getRecommendedCourses(
  articleSlug: string,
  tags: string[] = [],
  limit = 2,
  manualSlugs: string[] = []
): Course[] {
  const n = coursesData.length;

  if (manualSlugs.length > 0) {
    const manual = manualSlugs
      .map(slug => getCourseBySlug(slug))
      .filter((c): c is Course => !!c);
    if (manual.length > 0) return manual.slice(0, limit);
  }

  // Comparaison mot-à-mot (pas de sous-chaîne) pour éviter les faux positifs
  // du type "art" trouvé à l'intérieur de "artificielle", ou "Formation" (catégorie
  // quasi universelle) qui matcherait n'importe quel tag "Formation IA ...".
  const haystackWords = new Set(significantWords(tags.join(' ')));

  // Un point seulement si TOUS les mots significatifs de la phrase sont présents ;
  // si la phrase n'a aucun mot significatif (ex. catégorie "Formation"), elle ne
  // rapporte jamais de point.
  const phraseMatches = (phrase: string): boolean => {
    const words = significantWords(phrase);
    return words.length > 0 && words.every(w => haystackWords.has(w));
  };

  const scored = coursesData.map((course, index) => {
    let score = 0;

    const courseKeywords = courseKeywordsCache.get(course.id) ?? new Set<string>();
    courseKeywords.forEach(word => {
      if (!haystackWords.has(word)) return;
      const frequency = keywordFrequency.get(word) ?? 1;
      if (frequency === 1) score += 8;       // mot unique à ce cours : signal fort
      else if (frequency <= 3) score += 3;   // partagé par une poignée de cours
      // au-delà, le mot est trop générique pour compter
    });

    if (phraseMatches(course.category)) score += 1;
    course.objectives.forEach(o => { if (phraseMatches(o)) score += 1; });
    course.audiences.forEach(a => { if (phraseMatches(a)) score += 1; });

    return { course, index, score };
  }).sort((a, b) => b.score - a.score || a.index - b.index);

  const relevant = scored.filter(s => s.score > 0).map(s => s.course);

  const start = hashString(articleSlug) % n;
  const ring = Array.from({ length: n }, (_, i) => coursesData[(start + i) % n]);

  const seen = new Set<number>();
  const result: Course[] = [];
  for (const course of [...relevant, ...ring]) {
    if (seen.has(course.id)) continue;
    seen.add(course.id);
    result.push(course);
    if (result.length === limit) break;
  }
  return result;
}